import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get test launches for a specific test group
 * @param {Object} params - Parameters
 * @param {string} params.testGroupId - The test group ID
 * @param {number} [params.page=1] - Page number (starts at 1)
 * @param {number} [params.pageSize=25] - Number of items per page (max 100)
 * @param {string} [params.search=''] - Search term to filter launches by name
 * @param {string} [params.status='all'] - Filter by launch status (all, new, in_progress, completed, aborted)
 * @param {string} [params.trigger='all'] - Filter by trigger type (all, manual, scheduled)
 * @param {string} [params.launchType='all'] - Filter by launch type (all, AUTOMATED, MANUAL, COMPLETE)
 * @param {string} [params.environmentId='all'] - Filter by environment UUID or 'all'
 * @returns {Promise<Object>} Response with status and data
 */
export async function getTestLaunchesForGroup({
  testGroupId,
  page = 1,
  pageSize = 25,
  search = '',
  status = 'all',
  trigger = 'all',
  launchType = 'all',
  environmentId = 'all',
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test group ID is required",
      };
    }

    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search: search || '',
      status: status || 'all',
      trigger: trigger || 'all',
      launchType: launchType || 'all',
      environmentId: environmentId || 'all',
    });

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/${testGroupId}/launches?${params}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test launches:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Fetch detailed information about a specific test execution result
 * @param {Object} params - Parameters
 * @param {string} params.executionResultId - The execution result ID
 * @returns {Promise<Object>} Response with status and data
 */
export async function fetchExecutionDetails({ executionResultId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!executionResultId) {
      return {
        status: STATUS.ERROR,
        error: "Execution result ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/fetchExecutionDetails/${executionResultId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution details:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Re-execute a previously executed test case
 * @param {Object} params - Parameters
 * @param {string} params.executionResultId - The execution result ID to re-execute
 * @returns {Promise<Object>} Response with status and data
 */
export async function reExecuteTestcase({ executionResultId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!executionResultId) {
      return {
        status: STATUS.ERROR,
        error: "Execution result ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/re-execute-testcase`,
      { executionResultId },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error re-executing test case:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new test launch for a test group
 * @param {Object} params - Parameters
 * @param {string} params.testgroupId - The test group ID to launch
 * @param {string} [params.name] - Optional name for the test launch
 * @param {boolean} [params.isSequential=false] - Whether to execute test cases sequentially
 * @param {string} [params.launchType] - Launch type: AUTOMATED | MANUAL | COMPLETE
 * @param {string} [params.scheduleId] - Optional schedule ID when fired from a schedule
 * @param {Object} [params.config={}] - Launch configuration options
 * @param {string} [params.config.environmentId] - Environment ID to use for execution
 * @param {string} [params.config.llmModelId] - LLM Model ID to use for AI-powered test execution
 * @param {string} [params.hostname] - Optional hostname for the execution environment
 * @returns {Promise<Object>} Response with status and data
 */
export async function createTestLaunch({
  testgroupId,
  name,
  isSequential = false,
  launchType,
  scheduleId,
  config = {},
  hostname
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testgroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test group ID is required",
      };
    }

    const payload = {
      testgroupId,
      isSequential,
      config
    };

    if (name !== undefined && name !== null) {
      payload.name = name;
    }

    if (launchType !== undefined && launchType !== null) {
      payload.launchType = launchType;
    }

    if (scheduleId !== undefined && scheduleId !== null) {
      payload.scheduleId = scheduleId;
    }

    if (hostname !== undefined && hostname !== null) {
      payload.hostname = hostname;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/createTestLaunch`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating test launch:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get launch details summary including execution statistics
 * @param {Object} params - Parameters
 * @param {string} params.launchId - The launch ID
 * @returns {Promise<Object>} Response with status and data
 */
export async function getLaunchDetailsSummary({ launchId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!launchId) {
      return {
        status: STATUS.ERROR,
        error: "Launch ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/getLaunchDetailsSummary/${launchId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching launch details summary:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update the status of a test launch
 * @param {Object} params - Parameters
 * @param {string} params.launchId - The launch ID
 * @param {string} params.status - New status for the launch (only 'completed' or 'aborted' allowed)
 * @returns {Promise<Object>} Response with status and data
 */
export async function updateTestLaunchStatus({ launchId, status: newStatus } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!launchId) {
      return {
        status: STATUS.ERROR,
        error: "Launch ID is required",
      };
    }

    if (!newStatus) {
      return {
        status: STATUS.ERROR,
        error: "Status is required",
      };
    }

    if (newStatus.toLowerCase() !== 'completed' && newStatus.toLowerCase() !== 'aborted') {
      return {
        status: STATUS.ERROR,
        error: "Invalid status. Only 'completed' or 'aborted' are allowed.",
      };
    }

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/status/${launchId}`,
      { status: newStatus },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating test launch status:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Send an abort signal to stop a running test execution
 * @param {Object} params - Parameters
 * @param {string} params.executionResultId - The execution result ID to abort
 * @returns {Promise<Object>} Response with status and data
 */
export async function abortExecution({ executionResultId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!executionResultId) {
      return {
        status: STATUS.ERROR,
        error: "Execution result ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/test-launch/abortExecution`,
      { executionResultId },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error aborting execution:", error);
    return extractErrorInfo(error);
  }
}

